import { useEffect, useMemo, useState } from 'react'
import { Card, Col, Row, Typography, Button, List, Avatar, Skeleton, Space, Tooltip, Popconfirm, Form, Input, DatePicker, Switch, Popover, message } from 'antd'
import { EditOutlined, DeleteOutlined, PlusOutlined, InfoCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons'

import dayjs from 'dayjs';

import { SearchSelect } from './../../../../../component/Button';
import { useEgresadoContext } from './../../../context/EgresadoProvident';
import useExpAcademica from "./HookExpAcademica";
import functions from "./../../../shared/functions";


const { Title, Text } = Typography;


const ExperienciaAcademica = ({ id }) => {

  const { alumno } = useEgresadoContext();

  const {
    data,
    newData,
    setNewData,
    editing,
    startEdit,
    cancelEdit,
    saveRecord,
    deleteRecord,
    loading,
    catalogos
  } = useExpAcademica(alumno?.exp_academica);


  return (
    <Card id={id}>
      {
        (!editing && !newData) ?
          <ExperienciaAcademicaTable
            data={data}
            loading={loading}
            startEdit={startEdit}
            deleteRecord={deleteRecord}
            newData={newData}
            setNewData={setNewData}
          />
          :
          <FormExperienciaAcademica
            editing={editing}
            onSave={saveRecord}
            onCancel={cancelEdit}
            newData={newData}
            catalogos={catalogos}
          />
      }
    </Card>
  )
}

const ExperienciaAcademicaTable = ({ data, loading, startEdit, deleteRecord, setNewData }) => {

  return (
    <>
      <Row justify="space-between" align="middle">
        <Col>
          <Popover
            styles={{ root: { width: 250 } }}
            title="Experiencia Académica"
            content={
              <div style={{ textAlign: 'justify' }}>
                Puedes agregar cursos, talleres, diplomados u otra formación académica relevante. No incluyas el Bachillerato del CONALEP Estado de México.
              </div>
            }
            placement="right" icon={<InfoCircleOutlined />}>

            <Title level={5} strong>Experiencia Académica <QuestionCircleOutlined /></Title>
          </Popover>
        </Col>
        <Col>
          <Button
            type="text"
            style={{ color: '#7c0433' }}
            icon={<PlusOutlined />}
            onClick={() => setNewData(true)}
          >
            Agregar experiencia académica
          </Button>
        </Col>
        <Col span={24}>
          <List
            key={"experiencia_academica"}
            className="demo-loadmore-list"
            size="small"
            loading={loading}
            dataSource={data}
            itemLayout="horizontal"
            pagination={{ pageSize: 3 }}
            renderItem={(item, index) => (
              <List.Item
                key={item.id_exp}
                actions={[
                  <Space>
                    <Tooltip title="Editar experiencia académica">
                      <Button
                        key="edit"
                        icon={<EditOutlined />}
                        shape='circle'
                        size="small"
                        onClick={() => startEdit(item)}
                      />
                    </Tooltip>
                    {item?.sae != true &&
                      <Tooltip title="Eliminar experiencia académica">
                        <Popconfirm
                          title="¿Deseas eliminar esta experiencia académica?"
                          onConfirm={() => deleteRecord(item)}
                        >
                          <Button
                            key="delete"
                            disabled={item?.sae ? true : false}
                            icon={<DeleteOutlined />}
                            shape='circle'
                            size="small"
                            danger
                          />
                        </Popconfirm>
                      </Tooltip>
                    }
                  </Space>
                ]}
              >
                <Skeleton avatar title={false} loading={item.loading} active>
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        src={`https://api.dicebear.com/9.x/icons/svg?seed=Vivian&backgroundColor=7c0433`}
                      />
                    }
                    title={<strong>{item.puesto}</strong>}
                    description={
                      <Row gutter={[0, 4]}>
                        <Col span={24}>
                          {item?.nombre} en {item?.institucion}
                        </Col>
                        <Col span={24}>
                          {item?.fecha && functions.formatearRangoFechas(item?.fecha)} ({functions.calcularAniosMeses(item?.fecha)})
                        </Col>
                      </Row>
                    }
                  />
                </Skeleton>
              </List.Item>
            )}
          />
        </Col>
      </Row>
    </>
  )
}


const FormExperienciaAcademica = ({ editing, onSave, onCancel, catalogos }) => {
  const [form] = Form.useForm();
  const [isStudy, setIsStudy] = useState(false);
  const [fechaInicio, setFechaInicio] = useState(null);

  useEffect(() => {
    if (editing) {
      form.setFieldsValue(editing);
      setIsStudy(!editing?.fecha[1]);
      if (editing?.valor_cedula) {
        form.setFieldsValue({ tiene_cedula: true });
      }
    }
  }, [editing, form]);


  const catalogEstatusFormIfisStudy = useMemo(() => {
    if (editing?.sae) {
      return catalogos?.estado_formacion?.filter(formacion => formacion.solo_sae === editing.sae);
    }
    return catalogos?.estado_formacion?.filter(item => item.sigo_estudiando === isStudy);
  }, [catalogos?.estado_formacion, editing?.sae, isStudy]);

  const handleSave = async () => {
    const values = await form.validateFields();
    const response = await onSave(values);
    if (response) {
      form.resetFields();
      onCancel();
    }
  }

  const resetDocumentFields = () => {
    form.setFieldsValue({
      valor_otro: null,
      valor_titulo: null,
      tiene_cedula: false,
      valor_cedula: null
    });
  }

  const handleSwitchChange = (checked) => {
    form.setFieldsValue({ estatus_formacion: null });
    if (checked) {
      const fechaValue = form.getFieldValue('fecha')?.[0] || fechaInicio;
      form.setFieldsValue({ fecha: [fechaValue, null] });
    }
    setIsStudy(checked);
  }

  const handleCalendarChange = (dates) => {
    if (dates?.length && dates[0]) setFechaInicio(dates[0]);
  };

  const handleFormacionChange = () => {
    form.setFieldsValue({ doc_probatorio: null });
    resetDocumentFields();
  }

  const handleDocProbatorioChange = () => {
    resetDocumentFields();
  }

  const handleTienesCedulaChange = (checked) => {
    if (!checked) {
      form.setFieldsValue({ valor_cedula: null });
    }
  }

  const renderDocumentField = (docId, fieldName, label, placeholder, required, maxLeng) => {
    return (
      <Form.Item
        noStyle
        shouldUpdate={true}
      >
        {({ getFieldValue }) => {
          const docSeleccionado = getFieldValue('doc_probatorio');
          if (docSeleccionado === docId) {
            return (
              <Form.Item
                name={fieldName}
                label={label}
                rules={[
                  { required: required, message: `Por favor ingrese ${label.toLowerCase()}` },
                  { min: 5, max: maxLeng, message: 'Debe tener entre 3 y ' + maxLeng + ' caracteres' }
                ]}
              >
                <Input placeholder={placeholder} />
              </Form.Item>
            );
          }
          return null;
        }}
      </Form.Item>
    );
  }

  const editandoInfSae = editing?.sae;

  return (
    <>
      <Row>
        <Col>
          <Title level={5} strong>Agregar experiencia académica</Title>
        </Col>
        <Col>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            onFinishFailed={() => message.error("Por favor complete todos los campos")}
          >
            <Row gutter={16}>

              <Col span={24}>
                <Form.Item name="institucion" label="Institución" rules={[{ required: true, min: 3, max: 100, whitespace: true }]}>
                  <Input disabled={editandoInfSae} placeholder="Ejemplo:  U.A.E.M, U.N.A.M, Google." />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item name="formacion" label="Tipo de formación" rules={[{ required: true }]}>
                  <SearchSelect disabled={editandoInfSae} dataset={catalogos?.tipo_formacion} placeholder="Selecciona el tipo de formación que corresponde" />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item name="nombre" label="Grado de estudio, carrera, certificación" rules={[{ required: true, min: 3, max: 100, whitespace: true }]}>
                  <Input disabled={editandoInfSae} placeholder="Ejemplo: Ingeniería en Sistemas, Certificación en Marketing Digital" />
                </Form.Item>
              </Col>

              <Col md={24}>
                <Form.Item
                  name="fecha"
                  label="Periodo de estudio"
                  rules={[
                    { required: true, message: "Por favor seleccione el período de estudio" },
                    {
                      validator: (_, value) => {
                        if (!value || !Array.isArray(value)) {
                          return Promise.reject("Seleccione una fecha válida");
                        }
                        const [start, end] = value;
                        if (isStudy && !start) return Promise.reject("Debe seleccionar la fecha de inicio");
                        if (!isStudy && (!start || !end)) return Promise.reject("Debe seleccionar ambas fechas");
                        return Promise.resolve();
                      },
                    },
                  ]}>
                  <DatePicker.RangePicker
                    style={{ width: '100%' }}
                    placeholder={["Desde", "Hasta ahora"]}
                    format={"DD-MM-YYYY"}
                    allowEmpty={[false, isStudy]}
                    disabled={[(false || editandoInfSae), (isStudy || editandoInfSae)]}
                    onCalendarChange={handleCalendarChange}
                    disabledDate={(current) => {
                      const currentDate = dayjs();
                      return current && current.isAfter(currentDate.add(1, 'month'));
                    }}
                    renderExtraFooter={() => (
                      <>
                        <Text italic>¿Actualmente continúa con su formación?</Text>
                        <Switch onChange={handleSwitchChange} checked={isStudy} checkedChildren="Si" unCheckedChildren="No" />
                      </>
                    )}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  name="estatus_formacion"
                  label="Estatus de formación"
                  rules={[{ required: true }]}>
                  <SearchSelect
                    onChange={handleFormacionChange}
                    dataset={catalogEstatusFormIfisStudy}
                    placeholder="Selecciona el tipo de formación que corresponde" />
                </Form.Item>
              </Col>

              {/* Documento que avala */}
              <Col span={24}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) => prevValues.estatus_formacion !== currentValues.estatus_formacion}
                >
                  {({ getFieldValue }) => {
                    const estatusObj = catalogos?.estado_formacion?.find(({ id }) => id == getFieldValue('estatus_formacion'));
                    if (estatusObj?.requiere_doc_prob) {
                      return (
                        <Form.Item
                          name="doc_probatorio"
                          label="Documento que avala"
                          rules={[{ required: true }]}
                        >
                          <SearchSelect
                            onChange={handleDocProbatorioChange}
                            dataset={catalogos?.documento_probatorio}
                            placeholder="Selecciona el documento que avala"
                          />
                        </Form.Item>
                      );
                    }
                    return null;
                  }}
                </Form.Item>
              </Col>

              {/* Certificado / Constancia / Diploma */}
              <Col sm={24} md={12}>
                {renderDocumentField("Certificado / Constancia / Diploma", "valor_otro", "Número de registro, folio o clave del documento", "Número de registro, folio o clave del documento", false, 50)}
              </Col>

              {/* Cédula profesional */}
              <Col sm={24} md={12}>
                {renderDocumentField("Cédula profesional", "valor_cedula", "Cédula Profesional", "Ingresa tu cédula profesional", true, 12)}
              </Col>

              {/* Título profesional */}
              <Col sm={24} md={12}>
                {renderDocumentField("Título profesional", "valor_titulo", "Número de Título", "Ingresa el número de tu título", true, 12)}
              </Col>

              {/* Switch para cédula profesional (solo si es Título profesional) */}
              <Col sm={24} md={12}>
                <Form.Item
                  noStyle
                  shouldUpdate={true}
                >
                  {({ getFieldValue }) => {
                    if (getFieldValue('doc_probatorio') === "Título profesional") {
                      return (
                        <Form.Item name="tiene_cedula" label="¿Tienes cédula profesional?" valuePropName="checked">
                          <Switch onChange={handleTienesCedulaChange} checkedChildren="Sí" unCheckedChildren="No" />
                        </Form.Item>
                      );
                    }
                    return null;
                  }}
                </Form.Item>
              </Col>

              {/* Input cédula profesional */}
              <Col sm={24} md={12}>
                <Form.Item
                  noStyle
                  shouldUpdate={true}
                >
                  {({ getFieldValue }) => {
                    const docSeleccionado = getFieldValue('doc_probatorio');
                    const tienesCedula = getFieldValue('tiene_cedula');

                    if ((docSeleccionado === "Título profesional" && tienesCedula) || docSeleccionado === "Célula profesional") {
                      return (
                        <Form.Item
                          name="valor_cedula"
                          label="Cédula Profesional"
                          rules={[
                            { required: true, message: 'Por favor ingrese la cédula profesional' },
                            { min: 3, max: 10, message: 'Debe tener entre 3 y 10 caracteres' }
                          ]}
                        >
                          <Input placeholder="Ingresa tu cédula profesional" />
                        </Form.Item>
                      );
                    }
                    return null;
                  }}
                </Form.Item>
              </Col>

            </Row>

            <Row justify="end">
              <Space>
                <Button type="primary" htmlType="submit">
                  {editing ? "Guardar cambios" : "Agregar"}
                </Button>
                <Button onClick={() => {
                  onCancel();
                  form.resetFields();
                }}>
                  Cancelar
                </Button>
              </Space>
            </Row>
          </Form>
        </Col>
      </Row>
    </>
  )

}

export default ExperienciaAcademica;